import React from 'react';
import { useToasts } from 'react-toast-notifications';
import { Dialog, DialogContent } from '@material-ui/core';
import { PeculiarFortifyCertificates } from '@peculiar/fortify-webcomponents-react';
import * as pvtsutils from 'pvtsutils';
import * as pkijs from 'pkijs';
import * as asn1js from 'asn1js';
import * as cadesjs from 'cadesjs';

export async function signSerializedDocument(detail, serializedDocument) {
	const documentData = pvtsutils.Convert.FromString(serializedDocument);
	const provider = await detail.socketProvider.getCrypto(detail.providerId);
	const hashAlg = 'SHA-256';

	pkijs.setEngine(
		'newEngine',
		provider,
		new pkijs.CryptoEngine({
			name: '',
			crypto: provider,
			subtle: provider.subtle
		})
	);
	const crypto = pkijs.getCrypto();
	const cert = await provider.certStorage.getItem(detail.certificateId);
	const privateKey = await provider.keyStorage.getItem(detail.privateKeyId);
	const certRawData = await provider.certStorage.exportCert('raw', cert);

	const pkiCert = new pkijs.Certificate({
		schema: asn1js.fromBER(certRawData).result
	});
	const eSSCertIDv2 = new cadesjs.ESSCertIDv2();
	await eSSCertIDv2.fillValues({
		hashAlgorith: hashAlg,
		certificate: pkiCert
	});
	const signingCertificateV2 = new cadesjs.SigningCertificateV2({
		certs: [eSSCertIDv2]
	});

	const hashValue = await crypto.digest({ name: hashAlg }, documentData);

	const signedAttr = [];

	signedAttr.push(
		new pkijs.Attribute({
			type: '1.2.840.113549.1.9.3',
			values: [new asn1js.ObjectIdentifier({ value: '1.2.840.113549.1.7.5' })]
		})
	);

	signedAttr.push(
		new pkijs.Attribute({
			type: '1.2.840.113549.1.9.4',
			values: [new asn1js.OctetString({ valueHex: hashValue })]
		})
	);

	signedAttr.push(
		new pkijs.Attribute({
			type: '1.2.840.113549.1.9.5',
			values: [new asn1js.UTCTime({ valueDate: new Date() })]
		})
	);
	signedAttr.push(
		new pkijs.Attribute({
			type: '1.2.840.113549.1.9.16.2.47',
			values: [signingCertificateV2.toSchema()]
		})
	);

	const signedData = new pkijs.SignedData({
		version: 3,
		encapContentInfo: new pkijs.EncapsulatedContentInfo({
			eContentType: '1.2.840.113549.1.7.5'
		}),
		signerInfos: [
			new pkijs.SignerInfo({
				version: 1,
				sid: new pkijs.IssuerAndSerialNumber({
					issuer: pkiCert.issuer,
					serialNumber: pkiCert.serialNumber
				})
			})
		],
		certificates: [pkiCert]
	});
	signedData.signerInfos[0].signedAttrs = new pkijs.SignedAndUnsignedAttributes(
		{
			type: 0,
			attributes: signedAttr
		}
	);

	await signedData.sign(privateKey, 0, hashAlg);

	const cms = new pkijs.ContentInfo({
		contentType: '1.2.840.113549.1.7.2',
		content: signedData.toSchema(true)
	});
	const result = cms.toSchema().toBER(false);
	return pvtsutils.Convert.ToBase64(result);
}

export default function useFortifySigning({
	onSigned,
	failureMessage = 'Failed to sign invoice'
}) {
	const { addToast } = useToasts();
	const [state, setState] = React.useState({
		dialogShow: false,
		serializedDocument: null
	});
	const handleClose = () => {
		setState({ ...state, dialogShow: false });
	};
	const requestSignature = serializedDocument => {
		setState({ ...state, dialogShow: true, serializedDocument });
	};
	async function continueHandler(event) {
		let signature;
		try {
			signature = await signSerializedDocument(
				event.detail,
				state.serializedDocument
			);
		} catch (err) {
			handleClose();
			addToast(failureMessage, {
				appearance: 'error',
				autoDismiss: true
			});
		}
		if (signature != null) {
			handleClose();
			onSigned(signature);
		}
	}
	const signingDialog = (
		<Dialog onClose={handleClose} open={state.dialogShow} fullWidth>
			<DialogContent>
				<PeculiarFortifyCertificates
					filters={{
						onlyWithPrivateKey: true,
						onlySmartcards: true,
						expired: true
					}}
					onSelectionSuccess={continueHandler}
					onSelectionCancel={handleClose}
				/>
			</DialogContent>
		</Dialog>
	);
	return { requestSignature, signingDialog };
}
